/**
 * La scheda «Kit dei partner» (03/11/2026): i materiali che i partner possono mandare ai loro contatti, ciascuno coi
 * suoi messaggi pronti. Si creano, si correggono, si spengono e si riaccendono; non si cancellano (un messaggio già
 * mandato ha sempre da dove veniva). Il partner vede solo quelli accesi nella sua lingua, in quest'ordine.
 */

import React from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import type { MaterialeDoc } from '@shared/partner/materiali';
import { salvaMateriale, type ConId, type DatiProgrammaPartner } from '../../services/partnerConsoleService';
import { DialogoMateriale } from './DialogoMateriale';
import type { Esegui } from './esegui';

interface Props {
  dati: DatiProgrammaPartner;
  esegui: Esegui;
  onErrore: (messaggio: string) => void;
  onRicarica: () => void;
}

export const SchedaKit: React.FC<Props> = ({ dati, esegui, onErrore, onRicarica }) => {
  const { t } = useTranslation('partner');
  const [dialogo, setDialogo] = React.useState<{ materiale: ConId<MaterialeDoc> | null } | null>(null);
  const ordineNuovo = (dati.materiali.reduce((massimo, m) => Math.max(massimo, m.ordine), 0) || 0) + 10;

  const accendi = (m: ConId<MaterialeDoc>, attivo: boolean) => void esegui(() => salvaMateriale({
    materiale_id: m.id, nome: m.nome, descrizione: m.descrizione, tipo: m.tipo, destinazione: m.destinazione, settore: m.settore,
    messaggi: m.messaggi, lingua: m.lingua, attivo, ordine: m.ordine,
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: tokens.color.line, bgcolor: tokens.color.paper }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: tokens.color.ash, maxWidth: 720 }}>{t('kit.aiuto')}</Typography>
        <Box sx={{ flexShrink: 0 }}>
          <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => setDialogo({ materiale: null })} sx={{ textTransform: 'none' }}>
            {t('azioni.nuovo_materiale')}
          </Button>
        </Box>
      </Stack>
      {dati.materiali.length === 0 && <Typography variant="body2" sx={{ color: tokens.color.mist }}>{t('vuoti.materiali')}</Typography>}
      <Stack spacing={1}>
        {dati.materiali.map((m) => (
          <Stack
            key={m.id}
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ p: 1.5, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.color.lineSoft}`, opacity: m.attivo ? 1 : 0.6 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.nome}</Typography>
                <SoftChip label={t(`tipo_materiale.${m.tipo}`)} />
                {!m.attivo && <SoftChip label={t('kit.spento')} />}
              </Stack>
              {m.descrizione && <Typography variant="body2" sx={{ color: tokens.color.ash }}>{m.descrizione}</Typography>}
              <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 0.5 }}>
                {t('kit.destinazione')}: {m.destinazione ?? t('kit.iscrizione')}
                {' · '}{m.settore ? t('kit.settore', { settore: m.settore }) : t('kit.tutti_i_settori')}
                {' · '}{t('kit.lingua', { lingua: t(`lingue.${m.lingua === 'en' ? 'en' : 'it'}`) })}
              </Typography>
              <Box sx={{ mt: 1, p: 1.25, borderRadius: `${tokens.radius.sm}px`, bgcolor: tokens.color.bone, maxWidth: 640 }}>
                <Typography variant="caption" sx={{ color: tokens.color.ash, display: 'block' }}>{t('kit.breve')}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.messaggi.whatsapp_breve}</Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flexShrink: 0 }}>
              <Button size="small" onClick={() => setDialogo({ materiale: m })} sx={{ textTransform: 'none' }}>{t('azioni.modifica')}</Button>
              <Button size="small" onClick={() => accendi(m, !m.attivo)} sx={{ textTransform: 'none' }}>
                {m.attivo ? t('azioni.spegni') : t('azioni.riaccendi')}
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <DialogoMateriale
        aperto={!!dialogo}
        materiale={dialogo?.materiale ?? null}
        ordineNuovo={ordineNuovo}
        onChiudi={() => setDialogo(null)}
        onSalvato={onRicarica}
        onErrore={onErrore}
      />
    </Paper>
  );
};
